import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function useGoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;

    // Skip if no measurement ID configured
    if (!measurementId || measurementId === 'G-XXXXXXXXXX' || measurementId === 'your-google-analytics-id-here') {
      return;
    }

    // Initialize GA4 if not already loaded
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head?.appendChild(script);

      window.dataLayer = [];
      window.gtag = function gtag(...args) {
        window.dataLayer?.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false, // Manual page view tracking
        custom_map: {
          dimension1: 'performance_score',
          dimension2: 'bottleneck_type',
          metric1: 'load_time'
        }
      });
    }

    // Track page view with performance metrics
    const pageLoadTime = performance.now();
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_path: location?.pathname + location?.search,
        page_title: document.title,
        page_load_time: Math.round(pageLoadTime)
      });
    }

    // Initialize performance tracking for this page
    initializePerformanceTracking(location?.pathname);
    initializeEngagementTracking(location?.pathname);

  }, [location]);
}

// Performance Metrics Tracking (Web Vitals)
function initializePerformanceTracking(pagePath) {
  // Track Core Web Vitals using PerformanceObserver
  
  // 1. Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcpValue = lastEntry.renderTime || lastEntry.loadTime;
        
        trackPerformanceMetric('LCP', lcpValue, pagePath);
        
        // Detect bottleneck
        if (lcpValue > 4000) {
          trackBottleneck('LCP', lcpValue, 'critical', pagePath);
        } else if (lcpValue > 2500) {
          trackBottleneck('LCP', lcpValue, 'warning', pagePath);
        }
      });
      lcpObserver?.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.warn('LCP tracking not supported');
    }

    // 2. First Input Delay (FID)
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fidValue = entry.processingStart - entry.startTime;
          trackPerformanceMetric('FID', fidValue, pagePath);
          
          if (fidValue > 300) {
            trackBottleneck('FID', fidValue, 'critical', pagePath);
          } else if (fidValue > 100) {
            trackBottleneck('FID', fidValue, 'warning', pagePath);
          }
        });
      });
      fidObserver?.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      console.warn('FID tracking not supported');
    }

    // 3. Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        trackPerformanceMetric('CLS', clsValue, pagePath);
        
        if (clsValue > 0.25) {
          trackBottleneck('CLS', clsValue, 'critical', pagePath);
        } else if (clsValue > 0.1) {
          trackBottleneck('CLS', clsValue, 'warning', pagePath);
        }
      });
      clsObserver?.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      console.warn('CLS tracking not supported');
    }
  }

  // 4. First Contentful Paint (FCP) & Time to First Byte (TTFB)
  if (window.performance && window.performance?.getEntriesByType) {
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries?.length > 0) {
      const navEntry = navigationEntries?.[0];
      
      // TTFB
      const ttfb = navEntry?.responseStart - navEntry?.requestStart;
      trackPerformanceMetric('TTFB', ttfb, pagePath);
      if (ttfb > 800) {
        trackBottleneck('TTFB', ttfb, 'warning', pagePath);
      }
    }

    // FCP
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries?.find(entry => entry?.name === 'first-contentful-paint');
    if (fcpEntry) {
      trackPerformanceMetric('FCP', fcpEntry?.startTime, pagePath);
      if (fcpEntry?.startTime > 3000) {
        trackBottleneck('FCP', fcpEntry?.startTime, 'warning', pagePath);
      }
    }
  }

  // 5. Total Page Load Time
  window.addEventListener('load', () => {
    setTimeout(() => {
      const loadTime = performance.now();
      trackPerformanceMetric('page_load_complete', loadTime, pagePath);
      
      if (loadTime > 5000) {
        trackBottleneck('page_load', loadTime, 'critical', pagePath);
      } else if (loadTime > 3000) {
        trackBottleneck('page_load', loadTime, 'warning', pagePath);
      }
    }, 0);
  });
}

// User Engagement Tracking
function initializeEngagementTracking(pagePath) {
  let pageEnterTime = Date.now();
  let maxScrollDepth = 0;
  let interactionCount = 0;
  let isEngaged = false;

  // 1. Scroll Depth Tracking
  const trackScrollDepth = () => {
    const scrollTop = window.pageYOffset || document.documentElement?.scrollTop;
    const docHeight = document.documentElement?.scrollHeight - document.documentElement?.clientHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;
      
      // Track milestones
      if ([25, 50, 75, 90, 100]?.includes(scrollPercent)) {
        trackEngagementEvent('scroll_depth', {
          scroll_depth: scrollPercent,
          page_path: pagePath
        });
      }
    }
  };

  let scrollTimeout;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScrollDepth, 100);
  }, { passive: true });

  // 2. Time on Page Tracking
  const trackTimeOnPage = () => {
    const timeSpent = Math.round((Date.now() - pageEnterTime) / 1000);
    trackEngagementEvent('time_on_page', {
      time_seconds: timeSpent,
      page_path: pagePath,
      max_scroll_depth: maxScrollDepth,
      interaction_count: interactionCount
    });
  };

  // Track time on page at intervals
  const timeIntervals = [10, 30, 60, 120, 300]; // seconds
  timeIntervals?.forEach(interval => {
    setTimeout(() => {
      const currentTime = Math.round((Date.now() - pageEnterTime) / 1000);
      if (currentTime >= interval) {
        trackEngagementEvent('time_milestone', {
          milestone: interval,
          page_path: pagePath
        });
      }
    }, interval * 1000);
  });

  // 3. User Interaction Tracking
  const trackInteraction = (eventType) => {
    interactionCount++;
    if (!isEngaged && interactionCount >= 2) {
      isEngaged = true;
      trackEngagementEvent('user_engaged', {
        page_path: pagePath,
        time_to_engage: Math.round((Date.now() - pageEnterTime) / 1000)
      });
    }
  };

  // Track clicks
  document.addEventListener('click', (e) => {
    trackInteraction('click');
    
    // Track specific element clicks
    const target = e?.target;
    if (target?.tagName === 'BUTTON' || target?.tagName === 'A') {
      trackEngagementEvent('element_click', {
        element_type: target?.tagName?.toLowerCase(),
        element_text: target?.textContent?.substring(0, 50),
        page_path: pagePath
      });
    }
  }, { passive: true });

  // Track form interactions
  document.addEventListener('input', () => {
    trackInteraction('input');
  }, { passive: true });

  // 4. Page Exit Tracking
  const handlePageExit = () => {
    trackTimeOnPage();
  };

  window.addEventListener('beforeunload', handlePageExit);
  
  // Cleanup on route change (handled by useEffect cleanup)
  return () => {
    window.removeEventListener('beforeunload', handlePageExit);
  };
}

// Helper function to track performance metrics
function trackPerformanceMetric(metricName, value, pagePath) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'performance_metric', {
      metric_name: metricName,
      metric_value: Math.round(value),
      page_path: pagePath,
      timestamp: Date.now()
    });
  }
}

// Helper function to track bottlenecks
function trackBottleneck(bottleneckType, value, severity, pagePath) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'performance_bottleneck', {
      bottleneck_type: bottleneckType,
      bottleneck_value: Math.round(value),
      severity: severity,
      page_path: pagePath,
      timestamp: Date.now()
    });
    
    // Log to console for immediate visibility
    console.warn(`⚠️ Performance Bottleneck Detected [${severity?.toUpperCase()}]:`, {
      type: bottleneckType,
      value: `${Math.round(value)}ms`,
      page: pagePath,
      threshold: getThreshold(bottleneckType, severity)
    });
  }
}

// Helper function to track engagement events
function trackEngagementEvent(eventName, params) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ...params,
      timestamp: Date.now()
    });
  }
}

// Helper function to get threshold values
function getThreshold(metricType, severity) {
  const thresholds = {
    LCP: { warning: '2.5s', critical: '4.0s' },
    FID: { warning: '100ms', critical: '300ms' },
    CLS: { warning: '0.1', critical: '0.25' },
    FCP: { warning: '3.0s', critical: '5.0s' },
    TTFB: { warning: '800ms', critical: '1.5s' },
    page_load: { warning: '3.0s', critical: '5.0s' }
  };
  return thresholds?.[metricType]?.[severity] || 'N/A';
}

// Helper function to track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ...eventParams,
      page: window.location?.pathname
    });
  }
};

// Helper function to set user properties
export const setUserProperties = (userId, properties = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      ...properties
    });
  }
};

// Export performance tracking utilities
export const trackCustomPerformance = (metricName, value, metadata = {}) => {
  trackPerformanceMetric(metricName, value, window.location?.pathname);
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'custom_performance', {
      metric_name: metricName,
      metric_value: Math.round(value),
      ...metadata
    });
  }
};

export const trackUserEngagement = (action, details = {}) => {
  trackEngagementEvent('user_action', {
    action_type: action,
    ...details,
    page_path: window.location?.pathname
  });
};